import { Fragment, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, createRoutesFromElements, Route, useLocation } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { useAppEnvConfig } from '@connect-x-app/hybrid-state';
import { queryClient } from '@connect-x-app/web-data-access';
import { About } from '@connect-x-app/web-feature-app-config';
import { useSetupEnvConfig } from '@connect-x-app/web-state';
import { Spinner, Toastify } from '@connect-x-app/web-ui';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { App } from './App';
import { NavigationGuard } from './guards';
import { AxiosInterceptorProvider } from './interceptors';
import { LaunchDarklyWrapper } from './launchDarkly';

import '@connect-x-app/shared-translations';

// LaunchDarkly is on for web. Turned off, the app runs without it and every flag reads as off.
const FEATURE_FLAGS_ENABLED = true;
const FeatureFlags = FEATURE_FLAGS_ENABLED ? LaunchDarklyWrapper : Fragment;

const PublicAboutComponent = () => {
  return (
    <>
      <About showLicenses={false} />
      <Toastify />
    </>
  );
};

// The devtools button sits where neither app has its own controls: Connect's send button is bottom-right, Admin's
// Connected Turnaround button bottom-left.
const QueryDevtools = () => {
  const { pathname } = useLocation();

  return (
    <ReactQueryDevtools buttonPosition={pathname.startsWith('/admin') ? 'bottom-right' : 'bottom-left'} initialIsOpen={false} />
  );
};

const RootComponent = () => {
  const { error, loading } = useSetupEnvConfig();
  const { env } = useAppEnvConfig();

  if (loading) {
    return <Spinner overlay />;
  }

  if (error ?? !env) {
    return <div>Error: {error}</div>;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <FeatureFlags>
        <AxiosInterceptorProvider>
          <App />
          <NavigationGuard />
          <QueryDevtools />
        </AxiosInterceptorProvider>
      </FeatureFlags>
    </QueryClientProvider>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route element={<RootComponent />} path="*" />
      <Route element={<PublicAboutComponent />} path="about" />
    </>,
  ),
);

createRoot(document.getElementById('root')!).render(
  <Suspense fallback={<Spinner overlay />}>
    <RouterProvider router={router} />
  </Suspense>,
);
